import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    legalMatter: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      legalMatter: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Message Sent Successfully", {
          description: "Thank you for contacting us. We will respond to your enquiry shortly.",
        });
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          legalMatter: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast.error("Error", {
        description: "Failed to send message. Please call us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact Pattaya Solicitors | English Speaking Lawyer Pattaya +66 38 412-415"
        description="Contact Pattaya Solicitors - English speaking lawyers in Pattaya Thailand. Free consultation. Immigration lawyer, property lawyer, company registration, divorce attorney. Call +66 38 412-415."
        keywords="Contact Pattaya Solicitors, English Speaking Lawyer Pattaya, Pattaya Lawyers Thailand Contact, Law Firm Pattaya for Foreigners, Expat Solicitor Pattaya Phone"
        path="/contact"
        schemaType="contact"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#000A22] via-[#001438] to-[#000A22] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 bg-[#F5E7D2] text-[#000A22] px-5 py-2.5 rounded-full font-medium text-sm">
              <MessageCircle className="w-4 h-4 text-[#D4A74A]" />
              Get In Touch
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold">Contact Pattaya Solicitors</h1>
            <p className="text-xl text-gray-300">English Speaking Lawyers in Pattaya Thailand</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Information */}
              <div className="lg:col-span-1 space-y-6">
                <div>
                  <h2 className="text-3xl font-bold text-[#000A22] mb-6">Contact Information</h2>
                  <p className="text-gray-700 mb-8">
                    Reach out to us directly via phone, email, or visit our office in Pattaya.
                  </p>
                </div>

                <Card className="border-2 border-[#F5E7D2]">
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#F5E7D2] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-[#D4A74A]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#000A22] mb-2">Call Us</h3>
                        <div className="space-y-1 text-sm">
                          <p>
                            <a href="tel:+6638412415" className="text-[#D4A74A] hover:underline">
                              +66 (38) 412-415
                            </a>
                          </p>
                          <p>
                            <a href="tel:+66818383869" className="text-[#D4A74A] hover:underline">
                              +66 (81) 838-3869
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6 flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#F5E7D2] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#D4A74A]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#000A22] mb-2">Email</h3>
                        <p className="text-sm">
                          <a href="mailto:info@pattayasolicitors.com" className="text-[#D4A74A] hover:underline">
                            info@pattayasolicitors.com
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6 flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#F5E7D2] rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#D4A74A]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#000A22] mb-2">Office</h3>
                        <p className="text-sm text-gray-700">
                          20/173, Moo 10, South Pattaya Road,<br />
                          Pattaya City, Nongprue,<br />
                          Chonburi 20150
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="bg-[#F5E7D2] p-6 rounded-xl">
                  <h3 className="font-bold text-[#000A22] mb-2">Urgent Legal Matter?</h3>
                  <p className="text-sm text-gray-700 mb-4">
                    For urgent cases, please call us directly for immediate assistance.
                  </p>
                  <Button 
                    className="w-full bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-semibold"
                    asChild
                  >
                    <a href="tel:+6638412415">
                      <Phone className="w-4 h-4 mr-2" />
                      Call Now
                    </a>
                  </Button>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <Card className="border-2 border-[#F5E7D2] shadow-xl">
                  <CardContent className="p-8">
                    <h2 className="text-3xl font-bold text-[#000A22] mb-6">Send Us a Message</h2>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your full name"
                            className="border-2 focus:border-[#D4A74A]"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                            className="border-2 focus:border-[#D4A74A]"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone (Optional)</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+66 XX XXX XXXX"
                            className="border-2 focus:border-[#D4A74A]"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="legalMatter">Legal Matter *</Label>
                          <Select onValueChange={handleSelectChange} value={formData.legalMatter} required>
                            <SelectTrigger className="border-2 focus:border-[#D4A74A]">
                              <SelectValue placeholder="Select legal matter" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="visa">Visa & Immigration</SelectItem>
                              <SelectItem value="work-permit">Work Permits</SelectItem>
                              <SelectItem value="criminal">Criminal Law</SelectItem>
                              <SelectItem value="company">Company Registration</SelectItem>
                              <SelectItem value="will">Will and Testament</SelectItem>
                              <SelectItem value="property">Thai Property Law</SelectItem>
                              <SelectItem value="family">Divorce & Family Law</SelectItem>
                              <SelectItem value="civil">Civil Law & Litigation</SelectItem>
                              <SelectItem value="notarial">Notarial Services</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Please describe your legal matter..."
                          rows={6}
                          className="border-2 focus:border-[#D4A74A] resize-none"
                        />
                      </div>

                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                        <p className="text-sm text-blue-900">
                          <strong>Privacy Note:</strong> Information submitted is treated confidentially and in accordance with legal professional standards.
                        </p>
                      </div>

                      <Button 
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full md:w-auto bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-semibold px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl"
                      >
                        <Send className="w-5 h-5 mr-2" />
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-[#000A22] mb-8 text-center">Visit Our Office</h2>
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-[#F5E7D2]">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4086.23577649107!2d100.87590727523875!3d12.923160415938177!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x310296129616d609%3A0x612a448c57e9a27f!2sDMC%20Inter%20Law%20%26%20Consultants!5e1!3m2!1sen!2sth!4v1771046648008!5m2!1sen!2sth" 
                width="100%" 
                height="450" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="DMC Inter Law & Consultants Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
